import React from 'react';

import { FolderIcon } from '@vlabs/icons';
import { Badge, SideMenu, useWindowSize } from '@vlabs/uikit';
import { useFormContext } from 'react-hook-form';

import { useSearchPageTranslation } from '../../../i18n';
import st from '../Filters.module.sass';
import { EventFiltersWithLeftPopover, EventFiltersWithRightPopover } from '../side-menu';

export interface EventFilterFormProps {
  includedAttributes: string[];
}

function EventFilterForm(props: EventFilterFormProps): React.JSX.Element {
  const { t } = useSearchPageTranslation();
  const { includedAttributes = [] } = props;
  const { width } = useWindowSize();
  // @ts-expect-error требуется типизация useWindowSize
  const EventFilters = width < 1920 ? EventFiltersWithLeftPopover : EventFiltersWithRightPopover;

  const { getValues } = useFormContext();
  const getFilterValue = (name: string): unknown => (
    (getValues() as Record<string, unknown>)[name]
  );
  const getFilterArray = (name: string): unknown[] => (
    ((getValues() as Record<string, unknown>)[name] as unknown[] | undefined) ?? []
  );

  return (
    <React.Fragment>
      <SideMenu.MenuItem
        className={st.MenuGroup}
        data-testid="events.commonFilters"
        leftIcon={<FolderIcon />}
        value={t('filters.event_filters.general.title')}
      >
        <EventFilters.General.DateFilter
          badge={
            getFilterValue('create_time__lt') || getFilterValue('create_time__gte')
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="dateFilter"
          value={t('filters.event_filters.general.creation_date')}
        />
        <EventFilters.General.VideoSourceFilter
          badge={
            getFilterArray('sources').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="videoSourceFilter"
          value={t('filters.event_filters.general.video_source')}
        />
        <EventFilters.General.HandlersFilter
          badge={
            getFilterArray('handler_ids').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="handlersFilter"
          value={t('filters.event_filters.general.handlers')}
        />
        <EventFilters.General.SimilarityFilter
          badge={getFilterValue('threshold') ? <Badge shape="dot" /> : undefined}
          className={st.MenuGroup}
          testId="similarityFilter"
          value={t('filters.event_filters.general.similarity')}
        />
      </SideMenu.MenuItem>
      <div className={st.Divider} />
      <SideMenu.MenuItem
        className={st.MenuGroup}
        leftIcon={<FolderIcon />}
        value={t('filters.event_filters.comparing_attributes.title')}
      >
        <EventFilters.Comparing.ComparingAttributeFilter
          badge={
            getFilterValue('top_matching_candidates_label')
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="comparingAttributeFilter"
          value={t('filters.event_filters.comparing_attributes.comparing_attribute')}
        />
        <EventFilters.Comparing.TopSimilarObjectsFilter
          badge={
            getFilterValue('top_similar_object_ids')
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="topSimilarObjectsFilter"
          value={t('filters.event_filters.comparing_attributes.top_similar_objects')}
        />
      </SideMenu.MenuItem>
      <div className={st.Divider} />
      <SideMenu.MenuItem
        className={st.MenuGroup}
        data-testid="events.faceAttributesFilters"
        leftIcon={<FolderIcon />}
        value={t('filters.event_filters.face_attributes.title')}
      >
        <EventFilters.Face.GenderFilter
          badge={
            getFilterArray('gender').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="genderFilter"
          value={t('filters.event_filters.face_attributes.gender')}
        />
        <EventFilters.Face.AgeFilter
          badge={
            getFilterArray('age').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="ageFilter"
          value={t('filters.event_filters.face_attributes.age')}
        />
        <EventFilters.Face.EmotionFilter
          badge={
            getFilterArray('emotions').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="emotionFilter"
          value={t('filters.event_filters.face_attributes.emotion')}
        />
        <EventFilters.Face.MasksFilter
          badge={
            getFilterArray('masks').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="masksFilter"
          value={t('filters.event_filters.face_attributes.mask')}
        />
        <EventFilters.Face.LivenessFilter
          badge={
            getFilterArray('liveness').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="livenessFilter"
          value={t('filters.event_filters.face_attributes.liveness')}
        />
        <EventFilters.Face.DeepfakeFilter
          badge={
            getFilterArray('deepfake').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="deepfakeFilter"
          value={t('filters.event_filters.face_attributes.deepfake')}
        />
        <EventFilters.Face.FaceIdsFilter
          badge={
            getFilterArray('face_ids').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="faceIdsFilter"
          value={t('filters.event_filters.face_attributes.face_ids')}
        />
      </SideMenu.MenuItem>
      <div className={st.Divider} />
      <SideMenu.MenuItem
        className={st.MenuGroup}
        leftIcon={<FolderIcon />}
        value={t('filters.event_filters.body_attributes.title')}
      >
        <EventFilters.Body.ApparentGenderFilter
          badge={
            getFilterArray('apparent_gender').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="apparentGenderFilter"
          value={t('filters.event_filters.body_attributes.gender')}
        />
        <EventFilters.Body.ApparentAgeFilter
          badge={
            getFilterArray('apparent_age').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="apparentAgeFilter"
          value={t('filters.event_filters.body_attributes.age')}
        />
        <EventFilters.Body.UpperAttributesFilter
          badge={
            getFilterArray('upper_clothing_colors').length > 0 || getFilterArray('sleeve_lengths').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="upperAttributesFilter"
          value={t('filters.event_filters.body_attributes.upper_attributes')}
        />
        <EventFilters.Body.LowerAttributesFilter
          badge={
            getFilterArray('lower_garment_colors').length > 0 || getFilterArray('lower_garment_types').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="lowerAttributesFilter"
          value={t('filters.event_filters.body_attributes.lower_attributes')}
        />
        <EventFilters.Body.ShoesFilter
          badge={
            getFilterArray('shoes_apparent_colors').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="shoesFilter"
          value={t('filters.event_filters.body_attributes.shoes')}
        />
        <EventFilters.Body.HeadWearFilter
          badge={
            getFilterArray('headwear_apparent_colors').length > 0 || getFilterArray('headwear_states').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="headWearFilter"
          value={t('filters.event_filters.body_attributes.head_wear')}
        />
        <EventFilters.Body.BackpackFilter
          badge={
            getFilterArray('backpack_states').length > 0
              ? <Badge shape="dot" />
              : undefined
          }
          className={st.MenuGroup}
          testId="backpackFilter"
          value={t('filters.event_filters.body_attributes.backpack')}
        />
      </SideMenu.MenuItem>
      <div className={st.Divider} />
      <EventFilters.Location.LocationFilter
        badge={
          getFilterValue('areas') || getFilterValue('cities') || getFilterValue('districts')
            || getFilterValue('streets') || getFilterValue('house_numbers')
            || getFilterValue('latitude_delta') || getFilterValue('longitude_delta')
            || getFilterValue('origin_latitude') || getFilterValue('origin_longitude')
            ? <Badge shape="dot" />
            : undefined
        }
        className={st.MenuGroup}
        leftIcon={<FolderIcon />}
        popoverProps={{ includedAttributes }}
        testId="locationFilter"
        value={t('filters.event_filters.location.title')}
      />
    </React.Fragment>
  );
}

export { EventFilterForm };
